import React, { useState, useEffect } from 'react';
import axios from 'axios';
import './PendingMessagesModal.css';

const PendingMessagesModal = ({ isOpen, onClose, socket }) => {
  const [isProcessing, setIsProcessing] = useState(false);
  const [isSyncingHostinger, setIsSyncingHostinger] = useState(false);
  const [progress, setProgress] = useState({
    processed: 0,
    failed: 0,
    messages: 0,
    total: 0
  });
  const [status, setStatus] = useState('');
  const [stats, setStats] = useState(null);
  const [error, setError] = useState(null);
  const [syncDays, setSyncDays] = useState('today'); // 'today', 'all', o número de días
  const [customDate, setCustomDate] = useState('');

  // Cargar estadísticas al abrir
  useEffect(() => {
    if (isOpen) {
      loadStats();
    }
  }, [isOpen]);

  // Escuchar eventos de Socket.io
  useEffect(() => {
    if (!socket) return;

    const handleStarted = (data) => {
      setIsProcessing(true);
      setProgress({
        processed: 0,
        failed: 0,
        messages: 0,
        total: data.total
      });
      setStatus(`Iniciando procesamiento de ${data.total} webhook(s)...`);
      setError(null);
    };

    const handleProgress = (data) => {
      setProgress({
        processed: data.processed,
        failed: data.failed,
        messages: data.messages,
        total: data.total
      });
      setStatus(`Procesando... ${data.processed}/${data.total} webhooks`);
    };

    const handleCompleted = (data) => {
      setIsProcessing(false);
      setProgress({
        processed: data.processed,
        failed: data.failed,
        messages: data.messages,
        total: data.total
      });
      setStatus(`✅ Completado: ${data.messages} mensaje(s) recuperado(s)`);
      loadStats(); // Recargar estadísticas
    };

    const handleError = (data) => {
      setIsProcessing(false);
      setError(data.error || 'Error desconocido');
      setStatus('❌ Error al procesar');
    };

    const handleHostingerSyncStarted = () => {
      setIsSyncingHostinger(true);
      setStatus('🔄 Sincronizando con Hostinger...');
      setError(null);
    };

    const handleHostingerSyncCompleted = (data) => {
      setIsSyncingHostinger(false);
      const messagesText = data.totalMessages > 0 
        ? `${data.totalMessages} mensaje(s) recuperado(s)`
        : 'No se encontraron mensajes nuevos';
      setStatus(`✅ Sincronización completada: ${messagesText}`);
      loadStats(); // Recargar estadísticas
    };

    const handleHostingerSyncError = (data) => {
      setIsSyncingHostinger(false);
      setError(data.error || 'Error al sincronizar con Hostinger');
      setStatus('❌ Error en sincronización');
    };

    socket.on('pending-webhooks-processing-started', handleStarted);
    socket.on('pending-webhooks-progress', handleProgress);
    socket.on('pending-webhooks-processing-completed', handleCompleted);
    socket.on('pending-webhooks-processing-error', handleError);
    socket.on('hostinger-sync-started', handleHostingerSyncStarted);
    socket.on('hostinger-sync-completed', handleHostingerSyncCompleted);
    socket.on('hostinger-sync-error', handleHostingerSyncError);

    return () => {
      socket.off('pending-webhooks-processing-started', handleStarted);
      socket.off('pending-webhooks-progress', handleProgress);
      socket.off('pending-webhooks-processing-completed', handleCompleted);
      socket.off('pending-webhooks-processing-error', handleError);
      socket.off('hostinger-sync-started', handleHostingerSyncStarted);
      socket.off('hostinger-sync-completed', handleHostingerSyncCompleted);
      socket.off('hostinger-sync-error', handleHostingerSyncError);
    };
  }, [socket]);

  const loadStats = async () => {
    try {
      const token = localStorage.getItem('token');
      const apiUrl = process.env.REACT_APP_API_URL || '';
      const response = await axios.get(`${apiUrl}/api/system/pending-webhooks-stats`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.data.success) {
        setStats(response.data.stats);
      }
    } catch (error) {
      console.error('Error cargando estadísticas:', error);
      // Si hay error, establecer stats por defecto para que el botón funcione
      setStats({ pending: 0, processed: 0, failed: 0 });
    }
  };

  const handleProcess = async () => {
    try {
      setIsProcessing(true);
      setError(null);
      setStatus('Iniciando...');
      
      const token = localStorage.getItem('token');
      const apiUrl = process.env.REACT_APP_API_URL || '';
      const response = await axios.post(`${apiUrl}/api/system/process-pending-webhooks`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (response.data.success) {
        if (response.data.pendingCount === 0) {
          setStatus('✅ No hay webhooks pendientes');
          setIsProcessing(false);
        } else {
          setStatus(`Procesando ${response.data.pendingCount} webhook(s)...`);
        }
      } else {
        setError(response.data.error || 'Error al iniciar el procesamiento');
        setIsProcessing(false);
      }
    } catch (error) {
      setError(error.response?.data?.error || error.message || 'Error al procesar');
      setIsProcessing(false);
      setStatus('❌ Error');
    }
  };

  const handleSyncHostinger = async () => {
    try {
      setIsSyncingHostinger(true);
      setError(null);
      setStatus('🔄 Sincronizando con Hostinger...');
      
      const token = localStorage.getItem('token');
      const apiUrl = process.env.REACT_APP_API_URL || '';
      
      // Calcular días según la selección
      let days = 7; // Por defecto 7 días
      if (syncDays === 'today') {
        days = 1;
      } else if (syncDays === 'all') {
        days = 30; // Máximo 30 días
      } else if (syncDays === 'custom' && customDate) {
        // Calcular días desde la fecha seleccionada hasta hoy
        const selectedDate = new Date(customDate);
        const today = new Date();
        const diffTime = Math.abs(today - selectedDate);
        days = Math.ceil(diffTime / (1000 * 60 * 60 * 24)) + 1;
      } else if (!isNaN(parseInt(syncDays))) {
        days = parseInt(syncDays);
      }
      
      const response = await axios.post(
        `${apiUrl}/api/system/sync-hostinger`,
        { days: days },
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );

      if (response.data.success) {
        setStatus('🔄 Sincronización iniciada...');
        // El resultado llegará por Socket.io
      } else {
        setError(response.data.error || 'Error al sincronizar');
        setIsSyncingHostinger(false);
      }
    } catch (error) {
      setError(error.response?.data?.error || error.message || 'Error al sincronizar');
      setIsSyncingHostinger(false);
      setStatus('❌ Error');
    }
  };

  if (!isOpen) return null;

  const percentage = progress.total > 0 
    ? Math.round((progress.processed / progress.total) * 100) 
    : 0;

  return (
    <div className="pending-messages-modal-overlay" onClick={onClose}>
      <div className="pending-messages-modal-content" onClick={(e) => e.stopPropagation()}>
        <div className="pending-messages-modal-header">
          <h2>📥 Mensajes Pendientes de Hostinger</h2>
          <button className="pending-messages-modal-close" onClick={onClose}>×</button>
        </div>

        <div className="pending-messages-modal-body">
          {stats && (
            <div className="pending-messages-stats">
              <div className="stat-item">
                <span className="stat-label">Pendientes:</span>
                <span className="stat-value">{stats.pending || 0}</span>
              </div>
              <div className="stat-item">
                <span className="stat-label">Procesados:</span>
                <span className="stat-value">{stats.processed || 0}</span>
              </div>
              <div className="stat-item">
                <span className="stat-label">Fallidos:</span>
                <span className="stat-value error">{stats.failed || 0}</span>
              </div>
            </div>
          )}

          {error && (
            <div className="pending-messages-error">
              ❌ {error}
            </div>
          )}

          {isProcessing && (
            <div className="pending-messages-progress">
              <div className="progress-bar-container">
                <div 
                  className="progress-bar" 
                  style={{ width: `${percentage}%` }}
                ></div>
              </div>
              <div className="progress-text">
                {progress.processed} / {progress.total} webhooks procesados
              </div>
              {progress.messages > 0 && (
                <div className="progress-messages">
                  📨 {progress.messages} mensaje(s) recuperado(s)
                </div>
              )}
              {progress.failed > 0 && (
                <div className="progress-failed">
                  ⚠️ {progress.failed} fallido(s)
                </div>
              )}
            </div>
          )}

          {status && (
            <div className={`pending-messages-status ${isProcessing ? 'processing' : ''}`}>
              {status}
            </div>
          )}

          <div className="pending-messages-sync-section" style={{ marginBottom: '20px', padding: '15px', background: '#f9f9f9', borderRadius: '8px' }}>
            <h3 style={{ marginBottom: '15px', fontSize: '16px' }}>🔄 Recargar Mensajes de Hostinger</h3>
            
            <div style={{ marginBottom: '15px' }}>
              <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500' }}>
                Seleccionar período:
              </label>
              <select
                value={syncDays}
                onChange={(e) => setSyncDays(e.target.value)}
                disabled={isSyncingHostinger || isProcessing}
                style={{
                  width: '100%',
                  padding: '10px',
                  border: '2px solid #e0e0e0',
                  borderRadius: '5px',
                  fontSize: '14px',
                  marginBottom: '10px'
                }}
              >
                <option value="today">Hoy</option>
                <option value="1">Último día</option>
                <option value="2">Últimos 2 días</option>
                <option value="3">Últimos 3 días</option>
                <option value="7">Últimos 7 días</option>
                <option value="all">Todos (últimos 30 días)</option>
                <option value="custom">Fecha específica</option>
              </select>
              
              {syncDays === 'custom' && (
                <input
                  type="date"
                  value={customDate}
                  onChange={(e) => setCustomDate(e.target.value)}
                  max={new Date().toISOString().split('T')[0]}
                  disabled={isSyncingHostinger || isProcessing}
                  style={{
                    width: '100%',
                    padding: '10px',
                    border: '2px solid #e0e0e0',
                    borderRadius: '5px',
                    fontSize: '14px'
                  }}
                />
              )}
            </div>
            
            <button
              className="pending-messages-process-button"
              onClick={handleSyncHostinger}
              disabled={isSyncingHostinger || isProcessing}
              style={{
                width: '100%',
                padding: '12px',
                background: 'linear-gradient(135deg, #11998e 0%, #38ef7d 100%)',
                color: 'white',
                border: 'none',
                borderRadius: '5px',
                fontSize: '16px',
                fontWeight: '600',
                cursor: isSyncingHostinger || isProcessing ? 'not-allowed' : 'pointer',
                opacity: isSyncingHostinger || isProcessing ? 0.6 : 1
              }}
            >
              {isSyncingHostinger ? '🔄 Sincronizando con Hostinger...' : '🔄 Recargar desde Hostinger'}
            </button>
          </div>

          <div className="pending-messages-actions">
            <button
              className="pending-messages-process-button"
              onClick={handleProcess}
              disabled={isProcessing || isSyncingHostinger}
            >
              {isProcessing ? '🔄 Procesando...' : '🔄 Procesar Mensajes Pendientes'}
            </button>
            <button
              className="pending-messages-refresh-button"
              onClick={loadStats}
              disabled={isProcessing || isSyncingHostinger}
            >
              🔄 Actualizar Estadísticas
            </button>
          </div>

          <div className="pending-messages-info">
            <p>
              <strong>💡 Información:</strong>
            </p>
            <ul>
              <li>Los mensajes que llegaron mientras el panel estaba apagado se guardan en Hostinger</li>
              <li>Usa "Recargar desde Hostinger" para obtener mensajes guardados (puedes seleccionar el período)</li>
              <li>Usa "Procesar Mensajes Pendientes" para procesar webhooks pendientes en la base de datos</li>
              <li>Los mensajes recuperados aparecerán automáticamente en el panel</li>
              <li>El proceso se ejecuta en segundo plano y puedes ver el progreso en tiempo real</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PendingMessagesModal;

